import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  buildCategoryColumns,
  findCategoryNodeByPath,
  findCategoryPathIds,
  getCategoryRoots,
  isLeafCategoryNode,
} from '@/shein-platform/utils/sheinCategoryTree';

export default function CategoryCascader({
  categories,
  onChange,
  compact = false,
  selectedCategoryId = '',
  hideDetail = false,
  onColumnsChange,
}) {
  const roots = useMemo(() => getCategoryRoots(categories), [categories]);
  const [pathIds, setPathIds] = useState([]);
  const syncedSelectedRef = useRef('');

  useEffect(() => {
    const selectedKey = String(selectedCategoryId || '');
    if (!selectedKey) {
      if (syncedSelectedRef.current) {
        syncedSelectedRef.current = '';
        setPathIds([]);
      }
      return;
    }
    const next = findCategoryPathIds(roots, selectedKey);
    if (!next.length) return;
    if (syncedSelectedRef.current === selectedKey) return;
    syncedSelectedRef.current = selectedKey;
    setPathIds(next);
  }, [roots, selectedCategoryId]);

  const selectedPath = useMemo(
    () => findCategoryNodeByPath(roots, pathIds),
    [roots, pathIds],
  );

  const columns = useMemo(
    () => buildCategoryColumns(roots, pathIds),
    [roots, pathIds],
  );

  useLayoutEffect(() => {
    onColumnsChange?.(columns.length);
  }, [columns.length, onColumnsChange]);

  const selectedLeaf = selectedPath.length > 0
    ? selectedPath[selectedPath.length - 1]
    : null;

  const handleSelect = (columnIndex, node) => {
    const nextIds = [...pathIds.slice(0, columnIndex), String(node.category_id)];
    setPathIds(nextIds);
    const nextPath = findCategoryNodeByPath(roots, nextIds);
    const nextLeaf = nextPath.length > 0 ? nextPath[nextPath.length - 1] : null;
    const leafReady = nextLeaf && isLeafCategoryNode(nextLeaf) ? nextLeaf : null;
    onChange?.({
      pathIds: nextIds,
      path: nextPath,
      leaf: leafReady,
      categoryId: leafReady?.category_id ?? null,
      productTypeId: leafReady?.product_type_id ?? null,
      categoryPathLabel: nextPath.map((n) => n.category_name).filter(Boolean).join(' / '),
    });
  };

  if (roots.length === 0) {
    return <p className="shein-brand-ip__empty">{fixedUi("暂无可发商品分类")}</p>;
  }

  return (
    <div
      className={`shein-category-cascader${hideDetail ? ' shein-category-cascader--embedded' : ''}`}
      data-testid="shein-category-cascader"
    >
      <div className="shein-category-cascader__columns">
        {columns.map((column, columnIndex) => (
          <div key={`col-${columnIndex}`} className="shein-category-cascader__column">
            {column.map((node) => {
              const id = String(node.category_id);
              const active = pathIds[columnIndex] === id;
              const hasChildren = Array.isArray(node.children) && node.children.length > 0;
              return (
                <button
                  key={id}
                  type="button"
                  className={`shein-category-cascader__item${active ? ' shein-category-cascader__item--active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(columnIndex, node)}
                >
                  <span className="shein-category-cascader__label">
                    {node.category_name || fixedUiTpl('分类 {0}', id)}
                  </span>
                  {hasChildren ? <span className="shein-category-cascader__arrow">›</span> : null}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {hideDetail ? null : selectedLeaf && isLeafCategoryNode(selectedLeaf) ? (
        compact ? (
          <div className="shein-category-cascader__detail">
            <div className="shein-category-cascader__detail-title">{fixedUi("已选类目")}</div>
            <p className="shein-category-cascader__hint" style={{ margin: 0 }}>
              {selectedPath.map((n) => n.category_name).filter(Boolean).join(' / ')}
            </p>
          </div>
        ) : (
          <div className="shein-category-cascader__detail">
            <div className="shein-category-cascader__detail-title">{fixedUi("已选末级分类")}</div>
            <dl className="shein-category-cascader__detail-dl">
              <div>
                <dt>{fixedUi("分类路径")}</dt>
                <dd>{selectedPath.map((n) => n.category_name).filter(Boolean).join(' / ')}</dd>
              </div>
              <div>
                <dt>{fixedUi("分类 ID")}</dt>
                <dd><code>{selectedLeaf.category_id}</code></dd>
              </div>
              <div>
                <dt>{fixedUi("商品类型 ID")}</dt>
                <dd><code>{selectedLeaf.product_type_id ?? '—'}</code></dd>
              </div>
              <div>
                <dt>{fixedUi("上级分类 ID")}</dt>
                <dd>{selectedLeaf.parent_category_id ?? '—'}</dd>
              </div>
            </dl>
          </div>
        )
      ) : (
        <p className="shein-category-cascader__hint">
          {compact ? fixedUi("请逐级选择至末级类目。") : fixedUi("在左侧逐级选择分类，末级分类将显示发品所需信息。")}
        </p>
      )}
    </div>
  );
}
